import { Outlet, useMatches, useNavigate, useNavigation } from 'react-router-dom';

import { PageSkeleton } from '@/components/common/page-skeleton';
import { DialogHost } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import { AUTH_POLICY, useLogout } from '@/features/auth';
import { CommandAckBridge } from '@/features/devices';
import { useIdleTimeout } from '@/hooks/use-idle-timeout';
import { useAuth } from '@/providers/auth-provider';

import { PageContainer } from '../page-container';
import { TopNav } from '../top-nav';

import styles from './app-shell.module.scss';

export function AppShell() {
  const { rememberMe, clearSession } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();
  const matches = useMatches();
  const navigation = useNavigation();
  const fullBleed = matches.some(
    (m) => (m.handle as { fullBleed?: boolean } | undefined)?.fullBleed,
  );

  // While a lazy route chunk is loading, swap the page body for a skeleton so navigation
  // shows the page frame immediately instead of freezing on the previous page. Full-bleed
  // routes (e.g. the map) own their own loading UI, so they keep rendering the Outlet.
  const navigating = navigation.state === 'loading';

  useIdleTimeout({
    enabled: !rememberMe,
    timeoutMs: AUTH_POLICY.idleMs,
    onIdle: async () => {
      await logout.mutateAsync().catch(() => undefined);
      clearSession();
      navigate(routes.login);
      toast.info({ title: 'Your session has expired. Please log in.' });
    },
  });

  return (
    <DialogHost>
      <div className={styles.shell}>
        <CommandAckBridge />
        <TopNav />
        <main className={fullBleed ? styles.contentFullBleed : styles.content}>
          {fullBleed ? (
            <Outlet />
          ) : (
            <PageContainer>{navigating ? <PageSkeleton /> : <Outlet />}</PageContainer>
          )}
        </main>
      </div>
    </DialogHost>
  );
}
